import React, { useState } from 'react';
import { SESSION_DURATION_PRESETS, SessionDurationOption } from '../types';

interface SessionDurationPickerProps {
  selectedDuration: number; // in seconds, 0 = infinite / free play
  onSelectDuration: (seconds: number) => void;
  accentColor?: string;
  compact?: boolean;
}

export const SessionDurationPicker: React.FC<SessionDurationPickerProps> = ({
  selectedDuration,
  onSelectDuration,
  accentColor = '#38bdf8',
  compact = false
}) => {
  const [isCustomMode, setIsCustomMode] = useState(
    !SESSION_DURATION_PRESETS.some(p => p.seconds === selectedDuration) && selectedDuration > 0
  );
  const [customMinutes, setCustomMinutes] = useState(
    selectedDuration > 0 ? (selectedDuration / 60).toString() : '2'
  );

  const handlePresetClick = (seconds: number) => {
    setIsCustomMode(false);
    onSelectDuration(seconds);
  };

  const handleCustomApply = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const parsed = parseFloat(customMinutes);
    if (!isNaN(parsed) && parsed > 0) {
      const totalSec = Math.round(parsed * 60);
      onSelectDuration(Math.max(10, Math.min(3600, totalSec)));
    }
  };

  if (compact) {
    return (
      <div className="w-full space-y-2">
        {/* Responsive Grid of Preset Buttons */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 w-full">
          {SESSION_DURATION_PRESETS.map((preset) => {
            const isSelected = !isCustomMode && selectedDuration === preset.seconds;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => handlePresetClick(preset.seconds)}
                className={`w-full min-w-0 py-1.5 px-1 rounded-lg text-xs font-bold text-center transition-all active:scale-95 truncate flex items-center justify-center ${
                  isSelected
                    ? 'bg-sky-500 text-white shadow-md shadow-sky-500/30 ring-1 ring-white/20'
                    : 'bg-slate-800/90 text-slate-300 hover:text-white hover:bg-slate-700/90 border border-white/10'
                }`}
                title={preset.description}
              >
                <span className="truncate">{preset.label}</span>
              </button>
            );
          })}
          
          <button
            type="button"
            onClick={() => setIsCustomMode(!isCustomMode)}
            className={`w-full min-w-0 py-1.5 px-1 rounded-lg text-xs font-bold text-center transition-all active:scale-95 truncate flex items-center justify-center ${
              isCustomMode
                ? 'bg-sky-500 text-white shadow-md shadow-sky-500/30 ring-1 ring-white/20'
                : 'bg-slate-800/90 text-slate-300 hover:text-white hover:bg-slate-700/90 border border-white/10'
            }`}
            title="Enter a custom duration in minutes"
          >
            <span className="truncate">Custom</span>
          </button>
        </div>

        {/* Custom Input Drawer Sub-Row */}
        {isCustomMode && (
          <form 
            onSubmit={handleCustomApply} 
            className="flex items-center justify-between gap-2 p-2 bg-slate-950/80 rounded-xl border border-sky-500/30 w-full animate-fade-in"
          >
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <span className="text-[11px] text-slate-400 font-medium shrink-0">Duration:</span>
              <input
                type="number"
                min="0.5"
                max="60"
                step="0.5"
                value={customMinutes}
                onChange={(e) => setCustomMinutes(e.target.value)}
                onBlur={() => handleCustomApply()}
                className="w-16 px-2 py-1 bg-slate-900 border border-sky-400/50 text-white text-xs font-mono font-bold rounded-lg focus:outline-none focus:ring-1 focus:ring-sky-400"
                placeholder="2"
                aria-label="Custom duration in minutes"
              />
              <span className="text-[11px] text-slate-300 font-medium shrink-0">minutes</span>
            </div>
            
            <button
              type="submit"
              className="px-3 py-1 bg-sky-600 hover:bg-sky-500 active:scale-95 text-white text-xs font-bold rounded-lg transition shrink-0 shadow-sm shadow-sky-900/40"
            >
              Set
            </button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="w-full space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-2 shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-sky-400">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
          <span>Target Session Length</span>
        </label>
        <span className="text-[11px] font-mono text-sky-300 font-bold bg-sky-950/60 px-2.5 py-0.5 rounded-full border border-sky-500/30 whitespace-nowrap">
          {selectedDuration === 0
            ? 'Free Play (∞)'
            : selectedDuration < 60
            ? `${selectedDuration}s`
            : `${(selectedDuration / 60).toFixed(selectedDuration % 60 === 0 ? 0 : 1)} min`}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 w-full">
        {SESSION_DURATION_PRESETS.map((preset) => {
          const isSelected = !isCustomMode && selectedDuration === preset.seconds;
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => handlePresetClick(preset.seconds)}
              className={`p-2.5 rounded-xl border text-center transition-all flex flex-col items-center justify-center gap-0.5 active:scale-95 min-w-0 w-full ${
                isSelected
                  ? 'bg-sky-600/30 border-sky-400 text-white shadow-lg shadow-sky-950/50'
                  : 'bg-slate-900/80 border-white/10 text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
            >
              <span className={`text-xs font-bold truncate w-full ${isSelected ? 'text-sky-300' : 'text-white'}`}>
                {preset.label}
              </span>
              <span className="text-[9px] text-slate-400 leading-tight truncate w-full">
                {preset.seconds === 0 ? 'No Limit' : `${preset.seconds}s`}
              </span>
            </button>
          );
        })}
      </div>

      {/* Custom Duration Mode */}
      <div className="pt-1 flex flex-wrap sm:flex-nowrap items-center justify-between gap-2.5 bg-slate-900/60 p-2.5 rounded-xl border border-white/5 w-full">
        <button
          type="button"
          onClick={() => {
            setIsCustomMode(true);
            handleCustomApply();
          }}
          className={`text-xs font-semibold px-2.5 py-1 rounded-lg transition whitespace-nowrap ${
            isCustomMode ? 'bg-sky-500/20 text-sky-300 border border-sky-400/40' : 'text-slate-400 hover:text-white'
          }`}
        >
          Custom Length
        </button>

        {isCustomMode && (
          <form onSubmit={handleCustomApply} className="flex items-center gap-2 flex-1 sm:flex-initial min-w-0 justify-end">
            <div className="flex items-center gap-1.5 bg-slate-950 px-2 py-1 rounded-lg border border-white/10">
              <input
                type="number"
                min="0.5"
                max="60"
                step="0.5"
                value={customMinutes}
                onChange={(e) => setCustomMinutes(e.target.value)}
                className="w-12 bg-transparent text-white text-xs font-mono font-bold focus:outline-none text-right"
                placeholder="2"
              />
              <span className="text-xs text-slate-400">min</span>
            </div>
            <button
              type="submit"
              className="px-3 py-1 bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold rounded-lg transition shrink-0"
            >
              Set
            </button>
          </form>
        )}

        <span className="text-[10px] text-slate-400 hidden md:inline truncate">
          Auto-fades and saves when time finishes
        </span>
      </div>
    </div>
  );
};
